/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  Button,
  Badge,
  DataTable,
  Select,
  TextInput,
} from '@etherisc/ui-kit';
import { RefreshCw, DollarSign, Users, TrendingUp, Calendar } from 'lucide-react';
import { useCommissionSummary } from '../../hooks/insurance/useInsuranceBatch';
import { formatDate, formatCurrency } from '../../utils/formatters';

export const CommissionTrackingView = () => {
  const [filters, setFilters] = useState({
    agentId: '',
    currency: '',
    status: '',
    fromDate: '',
    toDate: ''
  });

  const { data: commissionData, refetch, isLoading } = useCommissionSummary(filters);

  const commissions = commissionData?.commissions || [];
  const summary = commissionData?.summary || {
    totalDue: '0',
    totalPaid: '0',
    totalOutstanding: '0',
    agentCount: 0
  };

  const columns = [
    {
      accessorKey: 'agentName',
      header: 'Agent',
      cell: ({ row }: { row: { original: any } }) => (
        <div>
          <div className="font-medium">{row.original.agentName}</div>
          <div className="text-sm text-muted-foreground">{row.original.agentCode}</div>
        </div>
      ),
    },
    {
      accessorKey: 'clientName',
      header: 'Client',
      cell: ({ row }: { row: { original: any } }) => (
        <div>
          <div className="font-medium">{row.original.clientName}</div>
          <div className="text-sm text-muted-foreground">{row.original.clientCode}</div>
        </div>
      ),
    },
    {
      accessorKey: 'costCenterName',
      header: 'Policy',
      cell: ({ row }: { row: { original: any } }) => (
        <div>
          <div className="font-medium">{row.original.costCenterName}</div>
          <div className="text-sm text-muted-foreground">{row.original.costCenterCode}</div>
        </div>
      ),
    },
    {
      accessorKey: 'premiumAmount',
      header: 'Premium',
      cell: ({ row }: { row: { original: any } }) => (
        <span className="font-semibold">
          {formatCurrency(row.original.premiumAmount, row.original.currency)}
        </span>
      ),
    },
    {
      accessorKey: 'commissionRate',
      header: 'Rate',
      cell: ({ row }: { row: { original: any } }) => (
        <span className="text-center font-medium">
          {(row.original.commissionRate * 100).toFixed(2)}%
        </span>
      ),
    },
    {
      accessorKey: 'commissionDue',
      header: 'Commission Due',
      cell: ({ row }: { row: { original: any } }) => (
        <span className="font-semibold text-blue-600">
          {formatCurrency(row.original.commissionDue, row.original.currency)}
        </span>
      ),
    },
    {
      accessorKey: 'commissionPaid',
      header: 'Commission Paid',
      cell: ({ row }: { row: { original: any } }) => (
        <span className="font-semibold text-green-600">
          {formatCurrency(row.original.commissionPaid, row.original.currency)}
        </span>
      ),
    },
    {
      accessorKey: 'commissionOutstanding',
      header: 'Outstanding',
      cell: ({ row }: { row: { original: any } }) => (
        <span className="font-semibold text-orange-600">
          {formatCurrency(row.original.commissionOutstanding, row.original.currency)}
        </span>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }: any) => {
        const status = row.original.status;
        const variant = status === 'paid' ? 'success' :
                      status === 'due' ? 'warning' :
                      status === 'overdue' ? 'destructive' : 'default';
        
        return (
          <Badge variant={variant}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'dueDate',
      header: 'Due Date',
      cell: ({ row }: any) => formatDate(row.original.dueDate),
    },
    {
      accessorKey: 'paidDate',
      header: 'Paid Date',
      cell: ({ row }: any) => row.original.paidDate ? formatDate(row.original.paidDate) : '-',
    }
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Commission Tracking
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Track agent commissions and payment status
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isLoading}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Due</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {formatCurrency(summary.totalDue, 'EUR')}
                    </p>
                  </div>
                  <Calendar className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Paid</p>
                    <p className="text-2xl font-bold text-green-600">
                      {formatCurrency(summary.totalPaid, 'EUR')}
                    </p>
                  </div>
                  <DollarSign className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Outstanding</p>
                    <p className="text-2xl font-bold text-orange-600">
                      {formatCurrency(summary.totalOutstanding, 'EUR')}
                    </p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-orange-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Active Agents</p>
                    <p className="text-2xl font-bold">{summary.agentCount}</p>
                  </div>
                  <Users className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="col-span-full text-sm font-medium text-gray-700 mb-2">Filter Commissions</h3>
            
            <Select
              label="Status"
              value={filters.status || 'none'}
              onValueChange={(value) => setFilters(prev => ({ 
                ...prev, 
                status: value === 'none' ? '' : value 
              }))}
              options={[
                { value: 'none', label: 'All Statuses' },
                { value: 'due', label: 'Due' },
                { value: 'paid', label: 'Paid' },
                { value: 'overdue', label: 'Overdue' },
                { value: 'cancelled', label: 'Cancelled' }
              ]}
            />

            <Select
              label="Currency"
              value={filters.currency || 'none'}
              onValueChange={(value) => setFilters(prev => ({ 
                ...prev, 
                currency: value === 'none' ? '' : value 
              }))}
              options={[
                { value: 'none', label: 'All Currencies' },
                { value: 'EUR', label: 'EUR' },
                { value: 'USD', label: 'USD' },
                { value: 'GBP', label: 'GBP' }
              ]}
            />

            <TextInput
              label="Agent ID"
              value={filters.agentId}
              onChange={(e) => setFilters(prev => ({ ...prev, agentId: e.target.value }))}
              placeholder="Search agent..."
            />

            <TextInput
              label="From Date"
              type="date"
              value={filters.fromDate}
              onChange={(e) => setFilters(prev => ({ ...prev, fromDate: e.target.value }))}
            />

            <TextInput
              label="To Date"
              type="date"
              value={filters.toDate}
              onChange={(e) => setFilters(prev => ({ ...prev, toDate: e.target.value }))}
            />

            <div className="flex items-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFilters({ 
                  agentId: '', 
                  currency: '', 
                  status: '', 
                  fromDate: '', 
                  toDate: '' 
                })}
              >
                Clear Filters
              </Button>
            </div>
          </div>

          {/* Commission Table */}
          <DataTable
            data={commissions}
            columns={columns}
            loading={isLoading}
          />

          {commissions.length === 0 && !isLoading && (
            <div className="text-center py-8 text-muted-foreground">
              No commission records found. Commissions will appear here once policies are sold.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
